/**
 * Bản đồ Leaflet hiển thị marker khu vực theo số lượng tin BĐS.
 *
 * Luồng dữ liệu:
 * - Nhận areas từ API (HomeSearchResults) và locationSearch từ URL.
 * - Gom areas thành nhóm tỉnh/phường; zoom >= ngưỡng thì hiện chi tiết phường.
 * - Click marker → gọi onAreaSelect để parent cập nhật bộ lọc URL.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { TEXT } from '../../shared/languages';
import { getFallbackMapView, getProvinceCenter, normalizeLocationKey } from '../../shared/utils/searchMapLocation';

const DEFAULT_MAP_CENTER = [16.0544, 108.2022];
const DEFAULT_MAP_ZOOM = 11;
/** Mức zoom từ đây trở lên sẽ hiển thị marker theo phường thay vì tỉnh */
const WARD_ZOOM_THRESHOLD = 12;

/** Tọa độ cố định cho một số phường đã biết (tránh offset ngẫu nhiên) */
const KNOWN_AREA_CENTERS = {
  danang: {
    haichau: [16.068, 108.2208],
    sontra: [16.1047, 108.252],
    nguhanhson: [16.0054, 108.2644],
  },
};

const createAreaKey = ({ province, ward }) =>
  [normalizeLocationKey(province), normalizeLocationKey(ward)].filter(Boolean).join(':');

/** Tạo offset lat/lng xác định từ chuỗi seed — phân tán marker khi không có tọa độ thật */
const getDeterministicOffset = (seedText, radius = 0.028) => {
  const seed = normalizeLocationKey(seedText);
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 9973;
  }

  const angle = (hash % 360) * (Math.PI / 180);
  const distance = radius * (0.45 + ((hash % 100) / 180));

  return [Math.sin(angle) * distance, Math.cos(angle) * distance];
};

/** Tính tâm marker: ưu tiên tọa độ đã biết, fallback offset từ tâm tỉnh */
const getAreaCenter = ({ province, ward }) => {
  const provinceKey = normalizeLocationKey(province);
  const wardKey = normalizeLocationKey(ward);
  const provinceCenter = getProvinceCenter(province) || DEFAULT_MAP_CENTER;

  if (!ward) {
    return provinceCenter;
  }

  const knownCenter = KNOWN_AREA_CENTERS[provinceKey]?.[wardKey];

  if (knownCenter) {
    return knownCenter;
  }

  const [latOffset, lngOffset] = getDeterministicOffset(`${province}-${ward}`);

  return [provinceCenter[0] + latOffset, provinceCenter[1] + lngOffset];
};

/** Gom nhiều bản ghi area API thành nhóm theo tỉnh hoặc phường, cộng dồn count */
const buildAreaGroups = (areas, level) => {
  const groups = new Map();

  areas.forEach((area) => {
    const province = area.province || '';
    const ward = level === 'ward' ? area.ward || '' : '';

    if (!province) {
      return;
    }

    const key = createAreaKey({ province, ward });
    const label = [ward, province].filter(Boolean).join(', ');
    const currentGroup = groups.get(key) || {
      key,
      province,
      ward,
      label,
      count: 0,
      center: getAreaCenter({ province, ward }),
    };

    currentGroup.count += Number(area.count || 0);
    groups.set(key, currentGroup);
  });

  return Array.from(groups.values());
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Tạo divIcon HTML cho marker khu vực — hiển thị tên và số tin */
const createAreaMarkerIcon = (areaGroup, isActive) => {
  const title = areaGroup.ward ? escapeHtml(areaGroup.ward) : escapeHtml(areaGroup.province);
  const subtitle = areaGroup.ward ? escapeHtml(areaGroup.province) : '';

  return L.divIcon({
    className: `area-map-marker-host${isActive ? ' is-active' : ''}`,
    html: `
      <button type="button" class="area-map-marker">
        <span class="area-map-marker__copy">
          <strong>${title}</strong>
          ${subtitle ? `<em>${subtitle}</em>` : ''}
        </span>
        <span class="area-map-marker__count">${areaGroup.count} tin</span>
      </button>
    `,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
};

export const AreaSearchMap = ({ areas = [], locationSearch = {}, onAreaSelect }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const markerByKeyRef = useRef(new Map());
  const zIndexCounterRef = useRef(1000);
  /** Ref giữ callback mới nhất — tránh re-bind marker khi onAreaSelect đổi */
  const onAreaSelectRef = useRef(onAreaSelect);
  /** Tránh fitBounds lặp lại khi cùng bộ dữ liệu */
  const lastFitKeyRef = useRef('');
  const fallbackView = useMemo(() => getFallbackMapView(locationSearch), [locationSearch]);
  const initialMapViewRef = useRef(fallbackView);
  const initialZoom = fallbackView?.zoom || DEFAULT_MAP_ZOOM;
  /** Zoom hiện tại — quyết định hiển thị nhóm tỉnh hay phường */
  const [currentZoom, setCurrentZoom] = useState(initialZoom);

  const activeAreaKey = createAreaKey(locationSearch);
  const shouldShowWardGroups = currentZoom >= WARD_ZOOM_THRESHOLD;
  const provinceGroups = useMemo(() => buildAreaGroups(areas, 'province'), [areas]);
  const wardGroups = useMemo(() => buildAreaGroups(areas, 'ward'), [areas]);
  const areaGroups = useMemo(
    () => (shouldShowWardGroups ? wardGroups : provinceGroups),
    [provinceGroups, shouldShowWardGroups, wardGroups]
  );
  /** Key thay đổi khi dữ liệu areas hoặc khu vực đang chọn đổi → trigger fitBounds */
  const fitKey = useMemo(
    () =>
      [
        activeAreaKey,
        areas.map((area) => `${area.province}-${area.ward}-${area.count}`).join(','),
      ].join('|'),
    [activeAreaKey, areas]
  );

  useEffect(() => {
    onAreaSelectRef.current = onAreaSelect;
  }, [onAreaSelect]);

  /** Khởi tạo bản đồ Leaflet một lần khi mount */
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    const initialMapView = initialMapViewRef.current;
    const map = L.map(mapContainerRef.current, {
      center: initialMapView?.center || DEFAULT_MAP_CENTER,
      zoom: initialMapView?.zoom || DEFAULT_MAP_ZOOM,
      scrollWheelZoom: false,
      tap: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    mapRef.current = map;

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  /** Đồng bộ state zoom React khi người dùng zoom trên map */
  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return undefined;
    }

    const handleZoomEnd = () => {
      setCurrentZoom(map.getZoom());
    };

    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, []);

  /** Vẽ lại markers và fit bounds khi nhóm khu vực hoặc bộ lọc thay đổi */
  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return undefined;
    }

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    markerByKeyRef.current = new Map();

    markersRef.current = areaGroups.map((areaGroup) => {
      const marker = L.marker(areaGroup.center, {
        icon: createAreaMarkerIcon(areaGroup, areaGroup.key === activeAreaKey),
        keyboard: false,
      });

      // Gắn click trực tiếp trong effect — tránh cảnh báo exhaustive-deps trên CI
      marker.on('add', () => {
        const button = marker.getElement()?.querySelector('.area-map-marker');

        if (!button) {
          return;
        }

        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', (event) => {
          L.DomEvent.stop(event);
          zIndexCounterRef.current += 1;
          marker.setZIndexOffset(zIndexCounterRef.current);
          onAreaSelectRef.current?.(areaGroup);
        });
      });

      marker.addTo(map);
      markerByKeyRef.current.set(areaGroup.key, marker);
      return marker;
    });

    const activeMarker = markerByKeyRef.current.get(activeAreaKey);
    if (activeMarker) {
      zIndexCounterRef.current += 1;
      activeMarker.setZIndexOffset(zIndexCounterRef.current);
    }

    if (markersRef.current.length) {
      if (lastFitKeyRef.current !== fitKey) {
        const bounds = L.latLngBounds(areaGroups.map((areaGroup) => areaGroup.center));
        map.fitBounds(bounds.pad(0.18), { maxZoom: 12 });
        lastFitKeyRef.current = fitKey;
      }
    } else if (fallbackView?.center) {
      map.setView(fallbackView.center, fallbackView.zoom || DEFAULT_MAP_ZOOM);
    }

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      markerByKeyRef.current = new Map();
    };
  }, [activeAreaKey, areaGroups, fallbackView, fitKey]);

  return (
    <div className="area-search-map">
      <div className="area-search-map-head">
        <strong>{TEXT.property.list.areaMapTitle}</strong>
        <span>{TEXT.property.list.areaMapHint}</span>
      </div>
      <div ref={mapContainerRef} className="area-search-map-canvas" />
    </div>
  );
};
